import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { ArrowRight, Lock, BookOpen, User } from "lucide-react";

export default function Collections() {
  const [colls, setColls] = useState([]);
  const [manifest, setManifest] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [c, m] = await Promise.all([api.collections(), api.manifest()]);
        setColls(c.collections || []);
        setManifest(m.series || []);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, []);

  const live = manifest.filter(s => s.status === "live");
  const coming = manifest.filter(s => s.status !== "live");

  return (
    <div className="animate-fade-up space-y-14" data-testid="page-collections">
      <div>
        <p className="label-caps text-gold/85">The Library</p>
        <h1 className="font-display text-4xl lg:text-5xl text-paper mt-2">Collections</h1>
        <p className="text-white/60 mt-2 max-w-2xl">
          The complete catalogue of Majalisul Ilm lecture series. Each series is a full study of a classical
          Islamic treatise, presented in the manhaj of the Salaf-us-Saliheen.
        </p>
      </div>

      {live.length > 0 && (
        <section>
          <div className="flex items-baseline gap-3 mb-6">
            <h2 className="font-display text-2xl text-paper">Available Now</h2>
            <span className="label-caps text-emerald-soft">{live.length} series</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="live-series-grid">
            {live.map(s => (
              <Link
                key={s.slug}
                to={`/series/${encodeURIComponent(s.name)}`}
                data-testid={`live-series-${s.slug}`}
                className="archival-card featured rounded-lg p-6 group"
              >
                <div className="flex items-start gap-5">
                  {s.thumbnail && (
                    <div className="w-20 h-20 rounded-md overflow-hidden bg-black/40 border border-white/5 shrink-0">
                      <img src={s.thumbnail} alt="" className={`w-full h-full ${s.cover_fit === "contain" ? "object-contain" : "object-cover"}`} />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-soft animate-pulse-glow" />
                      <span className="label-caps text-emerald-soft">Live · {s.live_count} lectures</span>
                    </div>
                    <h3 className="font-display text-2xl text-paper group-hover:text-emerald-soft transition-colors">{s.name}</h3>
                    <p className="text-sm text-gold/80 mt-1 italic">{s.translation}</p>
                    <p className="text-[13px] text-white/60 mt-3 leading-relaxed line-clamp-3">{s.description}</p>
                    <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1 text-xs text-white/50">
                      {s.author && <span className="inline-flex items-center gap-1"><BookOpen className="w-3 h-3 text-gold" /> {s.author}</span>}
                      {s.urdu_by && <span className="inline-flex items-center gap-1"><User className="w-3 h-3 text-emerald-soft" /> {s.urdu_by}</span>}
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {coming.length > 0 && (
        <section>
          <div className="flex items-baseline gap-3 mb-6">
            <h2 className="font-display text-2xl text-paper">Coming Soon</h2>
            <span className="label-caps text-gold/85">{coming.length} series in production</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="coming-series-grid">
            {coming.map(s => (
              <div
                key={s.slug}
                data-testid={`coming-series-${s.slug}`}
                className="archival-card rounded-lg p-6 opacity-85"
              >
                <div className="flex items-start gap-5">
                  <div className="w-20 h-20 rounded-md bg-gradient-to-br from-gold/10 to-emerald-glow/5 border border-white/5 shrink-0 flex items-center justify-center">
                    <Lock className="w-5 h-5 text-gold/70" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="label-caps text-gold/75">Coming soon · {s.total_planned} lectures planned</span>
                    </div>
                    <h3 className="font-display text-2xl text-paper">{s.name}</h3>
                    <p className="text-sm text-gold/70 mt-1 italic">{s.translation}</p>
                    <p className="text-[13px] text-white/55 mt-3 leading-relaxed line-clamp-3">{s.description}</p>
                    <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1 text-xs text-white/45">
                      {s.author && <span className="inline-flex items-center gap-1"><BookOpen className="w-3 h-3 text-gold/70" /> {s.author}</span>}
                      {s.urdu_by && <span className="inline-flex items-center gap-1"><User className="w-3 h-3 text-emerald-soft/70" /> {s.urdu_by}</span>}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {colls.length > 0 && (
        <section>
          <h2 className="font-display text-2xl text-paper mb-6">Explore by Discipline</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {colls.map(c => (
              <Link
                key={c.slug}
                to={`/discipline/${c.slug}`}
                data-testid={`collection-card-${c.slug}`}
                className="archival-card featured rounded-lg p-6 relative overflow-hidden group"
              >
                <div className="absolute -right-8 -bottom-8 w-32 h-32 rounded-full blur-2xl opacity-40 bg-emerald-glow" />
                <span className="label-caps text-white/50">Discipline</span>
                <h3 className="font-display text-2xl text-paper mt-2 group-hover:text-emerald-soft transition-colors">{c.title}</h3>
                {c.translation && <p className="text-sm text-gold/80 italic mt-1">{c.translation}</p>}
                <p className="text-sm text-white/60 mt-3 leading-relaxed line-clamp-3">{c.description}</p>
                <div className="mt-4 flex flex-wrap items-center gap-3 text-xs text-white/55">
                  <span className="chip">{c.series_count} series</span>
                  {c.live_series_count > 0 && (
                    <span className="chip active">{c.live_series_count} live</span>
                  )}
                  <span className="chip">{c.total_planned_lectures} lectures planned</span>
                </div>
                <div className="mt-4 inline-flex items-center gap-1 text-sm text-gold/90">
                  Open discipline <ArrowRight className="w-3.5 h-3.5" />
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
